// ── Скорость воспроизведения: значения, подписи и всплывающий выбор ───────────

import { useEffect, useRef, useState } from "react";
import { t } from "../i18n";
import { SANS } from "../themes";
import { usePalette } from "../ui/theme";
import { floatSurface } from '../ui/surface';

// ── SpeedPopup — выбор скорости воспроизведения ─────────────────────────────
// Скорость, как в Telegram: крупное число, плавный ползунок 0.5–2× (шаг 0.05),
// колёсико мыши и стрелки — по 0.05, быстрые кнопки. Скорость меняется сразу,
// пока тянете; окно закрывается нажатием мимо или Esc.
export const SPEED_PRESETS=[0.5,0.75,1,1.25,1.5,2];
export const SPEED_MIN=0.5,SPEED_MAX=2;
export const fmtSpeed=(s)=>`${Math.round((Number(s)||1)*100)/100}×`;
export const speedLabel=(s)=>s<0.95?t("Медленнее"):s<1.05?t("Обычная"):s<1.45?t("Быстрее"):t("Быстро");
export function SpeedPopup({speed,onSelect,onClose}){
  const TC=usePalette();
  const [live,setLive]=useState(null);
  const shown=live??(Number(speed)||1);
  const popupRef=useRef(null);
  const trackRef=useRef(null);
  const cur=useRef(shown);cur.current=shown;
  const sel=useRef(onSelect);sel.current=onSelect;
  const set=(v,commit)=>{
    v=Math.round(Math.min(SPEED_MAX,Math.max(SPEED_MIN,v))*20)/20;
    if(commit||v!==cur.current)sel.current?.(v);
    if(commit)setLive(null);else setLive(v);
  };
  const fromX=(x)=>{const r=trackRef.current?.getBoundingClientRect();return r&&r.width?SPEED_MIN+((x-r.left)/r.width)*(SPEED_MAX-SPEED_MIN):cur.current;};

  // Закрываем при нажатии ВНЕ окна (с задержкой, чтобы не поймать открывший клик) и по Esc.
  useEffect(()=>{
    const h=(e)=>{if(popupRef.current&&!popupRef.current.contains(e.target))onClose();};
    const k=(e)=>{if(e.key==="Escape")onClose();};
    const tm=setTimeout(()=>document.addEventListener("pointerdown",h,true),50);
    document.addEventListener("keydown",k);
    return()=>{clearTimeout(tm);document.removeEventListener("pointerdown",h,true);document.removeEventListener("keydown",k);};
  },[onClose]);
  // Колёсико мыши над окном: не пассивный обработчик, чтобы страница не прокручивалась.
  useEffect(()=>{
    const el=popupRef.current;if(!el)return undefined;
    const h=(e)=>{e.preventDefault();const st=e.deltaY<0?0.05:e.deltaY>0?-0.05:0;if(st)set(cur.current+st,true);};
    el.addEventListener("wheel",h,{passive:false});
    return()=>el.removeEventListener("wheel",h);
  },[]);// eslint-disable-line react-hooks/exhaustive-deps

  const dragging=live!=null;
  const prog=((shown-SPEED_MIN)/(SPEED_MAX-SPEED_MIN))*100;
  const oneX=((1-SPEED_MIN)/(SPEED_MAX-SPEED_MIN))*100;
  return(
    <div ref={popupRef} role="dialog" aria-label={t("Скорость")} data-panel="" onClick={e=>e.stopPropagation()}
      style={{position:"absolute",bottom:"calc(100% + 8px)",right:0,zIndex:600,width:248,boxSizing:"border-box",padding:"14px 16px 14px",
        ...floatSurface(TC),borderRadius:12,boxShadow:`0 8px 32px ${TC.shadowMd}`,fontFamily:SANS,color:TC.text}}>
      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,marginBottom:4}}>
        <span style={{fontSize:22,fontWeight:700,fontVariantNumeric:"tabular-nums"}}>{fmtSpeed(shown)}</span>
        <span style={{fontSize:12.5,color:TC.faint}}>{speedLabel(shown)}</span>
      </div>
      <div ref={trackRef} role="slider" tabIndex={0} aria-label={t("Скорость")} aria-valuemin={SPEED_MIN} aria-valuemax={SPEED_MAX} aria-valuenow={shown} aria-valuetext={fmtSpeed(shown)}
        onPointerDown={e=>{e.preventDefault();try{e.currentTarget.setPointerCapture(e.pointerId);}catch{}set(fromX(e.clientX),false);}}
        onPointerMove={e=>{if(dragging)set(fromX(e.clientX),false);}}
        onPointerUp={e=>{if(dragging)set(fromX(e.clientX),true);}}
        onPointerCancel={()=>{if(dragging)set(cur.current,true);}}
        onKeyDown={e=>{
          if(e.key==="ArrowRight"||e.key==="ArrowUp"){e.preventDefault();set(cur.current+0.05,true);}
          else if(e.key==="ArrowLeft"||e.key==="ArrowDown"){e.preventDefault();set(cur.current-0.05,true);}
          else if(e.key==="Home"){e.preventDefault();set(SPEED_MIN,true);}
          else if(e.key==="End"){e.preventDefault();set(SPEED_MAX,true);}
        }}
        style={{position:"relative",height:32,display:"flex",alignItems:"center",cursor:"pointer",touchAction:"none",outline:"none"}}>
        <div style={{position:"absolute",left:0,right:0,height:6,borderRadius:3,background:TC.border}}>
          <div style={{width:`${prog}%`,height:"100%",borderRadius:3,background:TC.blue}}/>
        </div>
        {/* Метка «1×» — обычная скорость */}
        <div aria-hidden="true" style={{position:"absolute",left:`${oneX}%`,top:"50%",width:2,height:12,borderRadius:1,background:TC.faint,transform:"translate(-50%,-50%)",opacity:.6}}/>
        <div style={{position:"absolute",left:`${prog}%`,top:"50%",width:16,height:16,borderRadius:"50%",background:TC.blue,
          transform:`translate(-50%,-50%) scale(${dragging?1.15:1})`,transition:"transform .12s",boxShadow:`0 0 0 3px ${TC.card}`,pointerEvents:"none"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:TC.faint,marginBottom:12,fontVariantNumeric:"tabular-nums"}}>
        <span>0.5×</span><span>2×</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
        {SPEED_PRESETS.map(s=>{
          const on=Math.abs(shown-s)<0.001;
          return(
            <button key={s} onClick={()=>set(s,true)} aria-pressed={on}
              style={{height:36,borderRadius:8,border:`1px solid ${on?TC.blue:TC.border}`,background:on?TC.bluePale:"transparent",color:on?TC.blueDark:TC.text,
                fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:SANS,fontVariantNumeric:"tabular-nums"}}>{fmtSpeed(s)}</button>
          );
        })}
      </div>
    </div>
  );
}
